import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ShieldCheck, ShieldOff, UserCog, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function UsuariosLimpeza() {
  const [users, setUsers] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: u }, { data: p }] = await Promise.all([
        api.get("/admin/cleaning-users"),
        api.get("/pousadas"),
      ]);
      setUsers(u || []);
      setPousadas(p || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar colaboradoras.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email, nome: u.nome,
      pousadas_ids: u.pousadas_ids || [],
      pode_liberar: !!u.pode_liberar,
      ativo: u.ativo !== false,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Informe o nome.");
    if (!form.email.trim()) return toast.error("Informe o e-mail Google.");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/admin/cleaning-users/${editingId}`, {
          nome: form.nome, pousadas_ids: form.pousadas_ids,
          pode_liberar: form.pode_liberar, ativo: form.ativo,
        });
        toast.success("Colaboradora atualizada");
      } else {
        await api.post("/admin/cleaning-users", {
          email: form.email.trim().toLowerCase(),
          nome: form.nome.trim(),
          pousadas_ids: form.pousadas_ids,
          pode_liberar: form.pode_liberar,
        });
        toast.success("Colaboradora cadastrada");
      }
      setOpen(false);
      setForm(emptyForm());
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar.");
    } finally { setBusy(false); }
  };

  const toggleAtivo = async (u) => {
    try {
      await api.patch(`/admin/cleaning-users/${u.id}`, { ativo: !u.ativo });
      toast.success(!u.ativo ? "Ativada" : "Inativada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao atualizar status.");
    }
  };

  const nomePousadas = (ids) => {
    if (!ids || ids.length === 0) return <span className="text-stone-400 text-xs">Todas</span>;
    return ids.map((id) => pousadas.find((p) => p.id === id)?.nome || "?").join(", ");
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operação · Limpeza"
        title="Colaboradoras de Limpeza"
        description="Cadastre e gerencie as colaboradoras autorizadas a acessar o Painel da Limpeza."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nova-colaboradora" onClick={openNew} className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                <Plus size={16} className="mr-2" /> Nova colaboradora
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {editingId ? "Editar colaboradora" : "Nova colaboradora"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Nome</Label>
                  <Input data-testid="input-colab-nome" value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div>
                  <Label>E-mail Google</Label>
                  <Input
                    type="email"
                    data-testid="input-colab-email"
                    value={form.email}
                    disabled={!!editingId}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="colaboradora@gmail.com"
                  />
                  <p className="text-[11px] text-stone-500 mt-1">
                    Deve ser a mesma conta Google usada no login. E-mail não pode ser alterado depois.
                  </p>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Building2 size={12} /> Pousadas autorizadas</Label>
                  <div className="mt-2 space-y-2 max-h-56 overflow-y-auto border border-stone-200 rounded-md p-3 bg-stone-50">
                    {pousadas.length === 0 && (
                      <p className="text-xs text-stone-500">Cadastre pousadas em /pousadas primeiro.</p>
                    )}
                    {pousadas.map((p) => {
                      const checked = form.pousadas_ids.includes(p.id);
                      return (
                        <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            data-testid={`chk-pousada-${p.id}`}
                            checked={checked}
                            onCheckedChange={(v) => {
                              setForm((f) => ({
                                ...f,
                                pousadas_ids: v
                                  ? [...f.pousadas_ids, p.id]
                                  : f.pousadas_ids.filter((x) => x !== p.id),
                              }));
                            }}
                          />
                          <span>{p.nome}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-stone-500 mt-1">
                    Se nenhuma for marcada, a colaboradora verá <strong>todas</strong> as pousadas.
                  </p>
                </div>
                <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                  <div>
                    <div className="text-sm font-medium text-stone-800 flex items-center gap-1">
                      <ShieldCheck size={14} /> Pode liberar quartos
                    </div>
                    <div className="text-[11px] text-stone-500">Autoriza liberar quartos após finalizar limpeza.</div>
                  </div>
                  <Switch
                    data-testid="switch-pode-liberar"
                    checked={form.pode_liberar}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, pode_liberar: v }))}
                  />
                </div>
                {editingId && (
                  <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                    <div>
                      <div className="text-sm font-medium text-stone-800">Ativa</div>
                      <div className="text-[11px] text-stone-500">Colaboradoras inativas não conseguem acessar o painel.</div>
                    </div>
                    <Switch
                      data-testid="switch-ativo"
                      checked={form.ativo}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: v }))}
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" disabled={busy} data-testid="btn-submit-colab" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                    {editingId ? "Salvar alterações" : "Cadastrar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">E-mail</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousadas</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pode liberar</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-stone-500 text-sm">Carregando…</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-stone-500 text-sm">Nenhuma colaboradora cadastrada.</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.id} data-testid={`colab-row-${u.id}`}>
                <TableCell className="font-medium text-stone-800">{u.nome}</TableCell>
                <TableCell className="text-stone-600 text-sm">{u.email}</TableCell>
                <TableCell className="text-stone-600 text-xs">{nomePousadas(u.pousadas_ids)}</TableCell>
                <TableCell>
                  {u.pode_liberar
                    ? <span className="inline-flex items-center gap-1 text-[11px] text-[#083A6B] bg-[#EAF0EA] px-2 py-0.5 rounded-full"><ShieldCheck size={11}/> Sim</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full"><ShieldOff size={11}/> Não</span>}
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => toggleAtivo(u)}
                    data-testid={`btn-toggle-ativo-${u.id}`}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${u.ativo ? "bg-[#EAF0EA] text-[#083A6B]" : "bg-stone-200 text-stone-600"}`}
                  >
                    {u.ativo ? "Ativa" : "Inativa"}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => openEdit(u)}
                    data-testid={`btn-edit-colab-${u.id}`}
                    className="text-stone-400 hover:text-[#083A6B] p-1"
                    aria-label="Editar"
                  >
                    <UserCog size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function emptyForm() {
  return { email: "", nome: "", pousadas_ids: [], pode_liberar: false, ativo: true };
}
